import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from 'config/firebase';
import moment from 'moment';

/**
 * Safely parse any Firebase or ISO date value to JS Date
 */
export const parseFirebaseDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      // ignore
    }
  }
  if (value.seconds !== undefined) {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

/**
 * Relative time formatting (e.g., "2 mins ago", "just now")
 */
export const formatRelativeTime = (value) => {
  const d = parseFirebaseDate(value);
  if (!d) return 'N/A';
  return moment(d).fromNow();
};

/**
 * Standard date & time formatting: "01:21 PM 31 Oct 2026"
 */
export const formatDateTime = (value) => {
  const d = parseFirebaseDate(value);
  if (!d) return 'N/A';
  return moment(d).format('hh:mm A, DD MMM YYYY');
};

/**
 * Clean phone number to last 10 digits for accurate matching
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

/**
 * Format Grade or Action Type display
 */
export const formatTypeDisplay = (type) => {
  if (!type) return 'N/A';
  const normalized = String(type).trim().toLowerCase();
  const typeMap = {
    offer_view: 'View Offers',
    demand_view: 'View Demands',
    offer_create: 'Create Offer',
    demand_create: 'Create Demand',
    offer: 'Offer Details',
    demand: 'Demand Details',
    'grade detail': 'Grade Details',
  };
  if (typeMap[normalized]) return typeMap[normalized];
  return normalized
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Fetch user documents from user_events_clicks for a specific user ID
 */
export const getUserEventsClicks = async (userId) => {
  if (!userId) return { primaryUserDoc: null, allDocs: [] };

  try {
    const clicksRef = collection(db, 'user_events_clicks');
    const qStr = query(clicksRef, where('user_id', '==', String(userId)));
    const snapshot = await getDocs(qStr);

    let { docs } = snapshot;
    if (docs.length === 0 && !Number.isNaN(Number(userId))) {
      const qNum = query(clicksRef, where('user_id', '==', Number(userId)));
      const snapNum = await getDocs(qNum);
      docs = snapNum.docs;
    }

    const mappedDocs = docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        userName: data.user_name || 'Guest User',
        userId: String(data.user_id || userId),
        mobileNumber: data.mobile_number || '',
        isGuest: Boolean(data.is_guest),
        totalClicks: Number(data.total_clicks || 0),
        lastButtonClicked: data.last_button_clicked || 'N/A',
        lastScreen: data.last_screen || 'N/A',
        lastActive: parseFirebaseDate(data.last_active),
        latestEvent: data.latest_event || null,
        raw: data,
      };
    });

    mappedDocs.sort((a, b) => {
      if (!a.lastActive && !b.lastActive) return 0;
      if (!a.lastActive) return 1;
      if (!b.lastActive) return -1;
      return b.lastActive - a.lastActive;
    });

    return {
      primaryUserDoc: mappedDocs[0] || null,
      allDocs: mappedDocs,
    };
  } catch (error) {
    console.error('Error fetching user_events_clicks:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for a specific user in user_events_clicks
 */
export const subscribeToUserEvents = (userId, onUpdate, onError) => {
  if (!userId) return () => {};

  try {
    const clicksRef = collection(db, 'user_events_clicks');
    const qStr = query(clicksRef, where('user_id', '==', String(userId)));

    return onSnapshot(
      qStr,
      (snapshot) => {
        const mappedDocs = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            userName: data.user_name || 'Guest User',
            userId: String(data.user_id || userId),
            mobileNumber: data.mobile_number || '',
            isGuest: Boolean(data.is_guest),
            totalClicks: Number(data.total_clicks || 0),
            lastButtonClicked: data.last_button_clicked || 'N/A',
            lastScreen: data.last_screen || 'N/A',
            lastActive: parseFirebaseDate(data.last_active),
            latestEvent: data.latest_event || null,
            raw: data,
          };
        });

        mappedDocs.sort((a, b) => {
          if (!a.lastActive && !b.lastActive) return 0;
          if (!a.lastActive) return 1;
          if (!b.lastActive) return -1;
          return b.lastActive - a.lastActive;
        });

        onUpdate({
          primaryUserDoc: mappedDocs[0] || null,
          allDocs: mappedDocs,
        });
      },
      (error) => {
        console.error('Realtime subscription error for user clicks:', error);
        if (onError) onError(error);
      },
    );
  } catch (err) {
    console.error('Failed to subscribe to user clicks:', err);
    return () => {};
  }
};

/**
 * Fetch sub-collection events for a user document (User Action Breakdown)
 */
export const getUserEventBreakdown = async (userDocId) => {
  if (!userDocId) return [];

  try {
    const eventsRef = collection(db, 'user_events_clicks', userDocId, 'events');
    const snapshot = await getDocs(eventsRef);

    const eventsList = [];
    snapshot.forEach((docSnap) => {
      // Exclude mirror document 00_LATEST_EVENT
      if (docSnap.id.startsWith('00_')) return;

      const data = docSnap.data();
      const lastClicked = parseFirebaseDate(
        data.last_clicked_at || data.timestamp,
      );

      eventsList.push({
        id: docSnap.id,
        buttonName: data.button_name || docSnap.id,
        screenName: data.screen_name || 'N/A',
        count: Number(data.count || 1),
        lastClickedAt: lastClicked,
        userFlow: data.user_flow || '',
        extraData: data.extra_data || null,
        raw: data,
      });
    });

    eventsList.sort((a, b) => {
      if (!a.lastClickedAt && !b.lastClickedAt) return 0;
      if (!a.lastClickedAt) return 1;
      if (!b.lastClickedAt) return -1;
      return b.lastClickedAt - a.lastClickedAt;
    });

    return eventsList;
  } catch (error) {
    console.error('Error fetching user event breakdown:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for a user's events subcollection
 */
export const subscribeToUserEventBreakdown = (
  userDocId,
  onUpdate,
  onError,
) => {
  if (!userDocId) return () => {};

  try {
    const eventsRef = collection(db, 'user_events_clicks', userDocId, 'events');
    return onSnapshot(
      eventsRef,
      (snapshot) => {
        const eventsList = [];
        snapshot.forEach((docSnap) => {
          if (docSnap.id.startsWith('00_')) return;

          const data = docSnap.data();
          const lastClicked = parseFirebaseDate(
            data.last_clicked_at || data.timestamp,
          );

          eventsList.push({
            id: docSnap.id,
            buttonName: data.button_name || docSnap.id,
            screenName: data.screen_name || 'N/A',
            count: Number(data.count || 1),
            lastClickedAt: lastClicked,
            userFlow: data.user_flow || '',
            extraData: data.extra_data || null,
            raw: data,
          });
        });

        eventsList.sort((a, b) => {
          if (!a.lastClickedAt && !b.lastClickedAt) return 0;
          if (!a.lastClickedAt) return 1;
          if (!b.lastClickedAt) return -1;
          return b.lastClickedAt - a.lastClickedAt;
        });

        onUpdate(eventsList);
      },
      (error) => {
        console.error('Realtime subscription error for events subcollection:', error);
        if (onError) onError(error);
      },
    );
  } catch (err) {
    console.error('Failed to subscribe to user events breakdown:', err);
    return () => {};
  }
};

/**
 * Fetch product / grade interest for a user from offers_views
 */
export const getUserOffersViews = async (userId) => {
  if (!userId) return [];

  try {
    const gradeDocs = await getDocs(collection(db, 'offers_views'));
    const gradePromises = gradeDocs.docs
      .filter((d) => d.id !== 'global_counts')
      .map(async (gradeDoc) => {
        try {
          const userRef = doc(
            db,
            'offers_views',
            gradeDoc.id,
            'users',
            String(userId),
          );
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            return {
              gradeName: gradeDoc.data().gradeName || gradeDoc.id,
              count: Number(data.count || 1),
              type: data.type || 'N/A',
              location: data.location || 'N/A',
              rate: data.rate || 'N/A',
              lastClicked: parseFirebaseDate(data.lastClicked),
              raw: data,
            };
          }
        } catch (e) {
          // ignore single lookup error
        }
        return null;
      });

    const results = (await Promise.all(gradePromises)).filter(Boolean);

    results.sort((a, b) => {
      if (!a.lastClicked && !b.lastClicked) return 0;
      if (!a.lastClicked) return 1;
      if (!b.lastClicked) return -1;
      return b.lastClicked - a.lastClicked;
    });

    return results;
  } catch (error) {
    console.error('Error fetching user offers views:', error);
    throw error;
  }
};

/**
 * Fetch login and OTP failures for a specific user mobile number
 */
export const getUserLoginFailures = async (phone, limitCount = 100) => {
  const cleanTarget = normalizePhone(phone);
  if (!cleanTarget) return [];

  try {
    const qFailures = query(
      collection(db, 'login_failures'),
      orderBy('timestamp', 'desc'),
      limit(limitCount),
    );
    const snapshot = await getDocs(qFailures);

    const failures = [];
    snapshot.forEach((docSnap) => {
      if (docSnap.id.startsWith('00_')) return;

      const data = docSnap.data();
      const docPhone = normalizePhone(data.mobile_number);

      if (
        docPhone &&
        (docPhone.includes(cleanTarget) || cleanTarget.includes(docPhone))
      ) {
        let parsedTime = parseFirebaseDate(data.timestamp || data.created_at);
        if (!parsedTime && docSnap.id.includes('_')) {
          try {
            const parts = docSnap.id.split('_');
            if (parts.length >= 3) {
              const datePart = parts[1];
              const timePart = parts[2].replace(/-/g, ':');
              parsedTime = moment(`${datePart}T${timePart}`).toDate();
            }
          } catch (e) {
            // ignore
          }
        }

        failures.push({
          id: docSnap.id,
          mobileNumber: data.mobile_number || '',
          failureStage: data.failure_stage || 'N/A',
          errorReason: data.error_reason || 'Unknown Error',
          screenName: data.screen_name || 'N/A',
          timestamp: parsedTime,
          raw: data,
        });
      }
    });

    failures.sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return b.timestamp - a.timestamp;
    });

    return failures;
  } catch (error) {
    console.error('Error fetching user login failures:', error);
    throw error;
  }
};

/**
 * Realtime listener for active users across the platform
 */
export const subscribeToActiveUsers = (callback, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'user_events_clicks'),
      orderBy('last_active', 'desc'),
      limit(limitCount),
    );

    return onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          userName: data.user_name || 'Guest User',
          userId: String(data.user_id || ''),
          mobileNumber: data.mobile_number || '',
          isGuest: Boolean(data.is_guest),
          totalClicks: Number(data.total_clicks || 0),
          lastButtonClicked: data.last_button_clicked || 'N/A',
          lastScreen: data.last_screen || 'N/A',
          lastActive: parseFirebaseDate(data.last_active),
          latestEvent: data.latest_event || null,
        };
      });
      callback(users);
    });
  } catch (err) {
    console.error('Failed to subscribe to all active users:', err);
    return () => {};
  }
};

/**
 * Fetch top viewed polymer grades across the entire platform
 */
export const getTopViewedGrades = async (limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'offers_views'),
      orderBy('totalCount', 'desc'),
      limit(limitCount),
    );
    const snapshot = await getDocs(q);

    const grades = [];
    snapshot.forEach((docSnap) => {
      if (docSnap.id === 'global_counts') return;
      const data = docSnap.data();
      grades.push({
        id: docSnap.id,
        gradeName: data.gradeName || docSnap.id,
        totalCount: Number(data.totalCount || 0),
        lastUpdated: parseFirebaseDate(data.lastUpdated),
      });
    });

    return grades;
  } catch (error) {
    console.error('Error fetching top viewed grades:', error);
    throw error;
  }
};

/**
 * Fetch global recent login failures across all users
 */
export const getGlobalLoginFailures = async (limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'login_failures'),
      orderBy('timestamp', 'desc'),
      limit(limitCount),
    );
    const snapshot = await getDocs(q);

    const failures = [];
    snapshot.forEach((docSnap) => {
      if (docSnap.id.startsWith('00_')) return;
      const data = docSnap.data();
      failures.push({
        id: docSnap.id,
        mobileNumber: data.mobile_number || 'N/A',
        failureStage: data.failure_stage || 'N/A',
        errorReason: data.error_reason || 'Unknown Error',
        screenName: data.screen_name || 'N/A',
        timestamp: parseFirebaseDate(data.timestamp || data.created_at),
      });
    });

    return failures;
  } catch (error) {
    console.error('Error fetching global login failures:', error);
    throw error;
  }
};
